/**
 * MVP module — engine input assembly (Phase F).
 * Gathers EngineInputs from live data and runs the pure engine.
 * Data-source map: docs/reference/mvp/IMPLEMENTATION_PLAN.md §3.
 *
 * v1 notes (documented limitations, all surfaced in the UI):
 *  - Entity = GROUP (subgroup/individual arrive with Phase C wiring).
 *  - Multi-strata counts are summed across strata (split-algorithm measures
 *    226/431/510 are approximations in projections — flagged in the detail).
 *  - multiSpecialty comes from hydrated npi_profiles (Phase C); false when the
 *    roster has not been hydrated yet.
 *  - allowedCharges = QPP practice eligibility value (hydration precedence
 *    chain arrives with Phase C).
 *  - CPB defaults to the practice's stored patient_complexity_bonus (exactly
 *    what live scoring shows) — overridable in the what-if panel.
 *  - Prior-year achievement comes from the uploaded score-submission file's
 *    category score (includes bonuses → `reconstructed` flag per plan §5.7).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api-client';
import { keepPreviousSameEntity, qk } from '../query-keys';
import {
  useMipsScores, useMipsWeights, usePerformanceSummaries, useIaScore,
  useCostDashboard, usePaymentAdjustmentConfig, usePracticeEligibility,
} from '@/hooks/queries/useMipsQueries';
import {
  useNpiProfiles, useResolvedFinancials, useSubgroups, useSubgroupSummaries,
} from '@/hooks/queries/useMvpQueries';
import { MipsDataService } from '../mips-data-service';
import { rankPathways } from './engine';
import type {
  Assumptions, CostMeasureScore, EngineDeps, EngineInputs, MeasureCounts,
  PopHealthEntry, RankedReport,
} from './types';

// ---------------------------------------------------------------------------
// Extra fetches not covered by the shared hooks
// ---------------------------------------------------------------------------

function useIaSelections(practiceId: number | string | undefined, year: number) {
  return useQuery<any[]>({
    queryKey: ['mvp', 'ia-selections', practiceId ?? '', year],
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/ia/selections?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<any[]>(2, String(practiceId ?? '')),
  });
}

function usePracticeSettings(practiceId: number | string | undefined, year: number) {
  const id = practiceId != null ? String(practiceId) : '';
  return useQuery<any>({
    queryKey: qk.practiceSettings(id || 'none', year),
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/settings?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, id),
  });
}

function useScoreSubmission(practiceId: number | string | undefined, year: number) {
  return useQuery<any>({
    queryKey: ['mvp', 'score-submission', practiceId ?? '', year],
    // null = no submission uploaded for this registry year (handled downstream)
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/score-submissions/${year}`).catch(() => null),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
    staleTime: 10 * 60_000,
  });
}

function usePopHealthEntries(practiceId: number | string | undefined, year: number) {
  return useQuery<any[]>({
    queryKey: ['mvp', 'pop-health', practiceId ?? '', year],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/pop-health?year=${year}`).catch(() => []),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<any[]>(2, String(practiceId ?? '')),
  });
}

// ---------------------------------------------------------------------------
// Shape normalization (defensive at the fetch boundary — anti-patterns rule)
// ---------------------------------------------------------------------------

/** Summaries → pre-aggregated per-measure counts (strata summed; see v1 notes). */
export function summariesToCounts(summaries: any[]): MeasureCounts[] {
  const rows: MeasureCounts[] = [];
  for (const s of summaries ?? []) {
    const measureId = s.measureId ?? s.measure_id;
    const submissionMethod = s.submissionMethod ?? s.submission_method ?? 'MIPS CQM';
    if (!measureId) continue;
    let c = { ip: 0, ex: 0, den: 0, met: 0, exc: 0, not: 0 };
    if (s.counts) {
      c = {
        ip: Number(s.counts.ip) || 0, ex: Number(s.counts.ex) || 0, den: Number(s.counts.den) || 0,
        met: Number(s.counts.met) || 0, exc: Number(s.counts.exc) || 0, not: Number(s.counts.not) || 0,
      };
    } else if (Array.isArray(s.strata) && s.strata.length > 0) {
      for (const st of s.strata) {
        c.ip += Number(st.ip) || 0; c.ex += Number(st.ex) || 0; c.den += Number(st.den) || 0;
        c.met += Number(st.met) || 0; c.exc += Number(st.exc) || 0; c.not += Number(st.not) || 0;
      }
    }
    if (c.den + c.met + c.not + c.exc === 0) continue; // placeholder rows carry no signal
    rows.push({ measureId: String(measureId), submissionMethod: String(submissionMethod), ...c });
  }
  return rows;
}

/** Cost dashboard → engine cost scores (normalize field-name drift defensively). */
export function costDashboardToScores(dashboard: any): CostMeasureScore[] {
  const measures = dashboard?.measures ?? [];
  return measures.map((m: any) => {
    const points = m.points ?? m.decileScore ?? null;
    const caseMinMet = m.meetsCaseMinimum ?? m.meetsMinimum ?? m.meets_case_minimum ?? (points != null && points > 0);
    return {
      measureId: String(m.measureId ?? m.measure_id ?? m.id ?? ''),
      points: points != null ? Number(points) : null,
      caseMinMet: !!caseMinMet,
    };
  }).filter((m: CostMeasureScore) => m.measureId);
}

/** Score-submission measures JSONB → pop-health entries (479/484 Administrative Claims rows).
 * Verified against the real CMS sample (Phase G): 479 row stores
 * collection_type 'Administrative Claims', performance_rate '0.172672',
 * decile_score 1.6451 (parseNumeric'd by scoreSubmissions.js). */
export function extractPopHealth(submission: any, manualEntries: any[]): {
  m479: PopHealthEntry | null; m484: PopHealthEntry | null;
} {
  const out: { m479: PopHealthEntry | null; m484: PopHealthEntry | null } = { m479: null, m484: null };
  const rows: any[] = Array.isArray(submission?.measures) ? submission.measures : [];
  for (const id of ['479', '484'] as const) {
    const candidates = rows.filter(r => String(r.measure_id) === id);
    // CMS publishes 479/484 as Administrative Claims — prefer that row if a file
    // ever carries the id under another collection type.
    const row = candidates.find(r => r.collection_type === 'Administrative Claims') ?? candidates[0];
    if (row && row.decile_score != null) {
      out[`m${id}`] = { points: Number(row.decile_score), caseMinMet: true, source: 'score_submission' };
      continue;
    }
    const manual = (manualEntries ?? []).find(e => String(e.measure_id) === id);
    if (manual) {
      out[`m${id}`] = {
        points: manual.score_points != null ? Number(manual.score_points) : null,
        caseMinMet: !!manual.case_min_met,
        source: 'manual',
      };
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface MvpPathwayReportState {
  report: RankedReport;
  loading: boolean;
  ready: boolean;
  meta: {
    mvpDatasetAvailable: boolean;
    measuresWithData: number;
    popHealthSource: string;
    priorYearAvailable: boolean;
    dollarsConfigured: boolean;
    allowedCharges: number | null;
    /** Always-visible "inputs in use" strip (Configuration is the source of defaults). */
    inputsInUse: {
      isSmallPractice: boolean;
      piSource: string;
      iaSource: string;
      chargesSource: string;
      cpb: number;
    };
  };
}

const EMPTY_INPUTS_IN_USE = {
  isSmallPractice: false, piSource: '—', iaSource: '—', chargesSource: '—', cpb: 0,
};

export function useMvpPathwayReport(
  practiceId: number | string | undefined,
  year: number,
  assumptions: Assumptions,
  /** C2: run the report at subgroup scope — subgroup-derived quality counts,
   * roster-summed dollars; cost/PI/pop-health stay group-level (CMS rule). */
  subgroupId: number | null = null,
): MvpPathwayReportState {
  const summariesQ = usePerformanceSummaries(practiceId, year, 'GROUP');
  const subgroupSummariesQ = useSubgroupSummaries(practiceId, subgroupId, year);
  const subgroupsQ = useSubgroups(practiceId, year);
  const financialsQ = useResolvedFinancials(practiceId, year);
  const scoresQ = useMipsScores(practiceId, year);
  const weightsQ = useMipsWeights(practiceId, year);
  const iaScoreQ = useIaScore(practiceId, year, null);
  const iaSelectionsQ = useIaSelections(practiceId, year);
  const costQ = useCostDashboard(practiceId, year, null);
  const settingsQ = usePracticeSettings(practiceId, year);
  const eligibilityQ = usePracticeEligibility(practiceId, year);
  const submissionQ = useScoreSubmission(practiceId, year);
  const popHealthManualQ = usePopHealthEntries(practiceId, year);
  const dollarsConfigQ = usePaymentAdjustmentConfig(year);
  // Phase C specialty layer: multiSpecialty from hydrated npi_profiles.
  // Deliberately NOT in `loading` — a hiccup here degrades to multiSpecialty=false
  // (advisory badge), never blocks the report.
  const npiProfilesQ = useNpiProfiles(practiceId, year);

  const loading =
    summariesQ.isPending || scoresQ.isPending || weightsQ.isPending || iaScoreQ.isPending ||
    iaSelectionsQ.isPending || costQ.isPending || settingsQ.isPending || eligibilityQ.isPending ||
    submissionQ.isPending || popHealthManualQ.isPending || dollarsConfigQ.isPending ||
    (subgroupId != null && subgroupSummariesQ.isPending);

  return useMemo<MvpPathwayReportState>(() => {
    const dataService = MipsDataService.getInstance();
    const mvpDatasetAvailable = dataService.hasMvpDataset(year);

    if (loading || !practiceId || !mvpDatasetAvailable) {
      return {
        report: [], loading, ready: false,
        meta: {
          mvpDatasetAvailable, measuresWithData: 0, popHealthSource: 'none',
          priorYearAvailable: false, dollarsConfigured: false, allowedCharges: null,
          inputsInUse: EMPTY_INPUTS_IN_USE,
        },
      };
    }

    const w = weightsQ.data ?? {};
    const s = scoresQ.data ?? {};
    const settings = settingsQ.data ?? {};
    const submission = submissionQ.data;

    // PI pct (0-100). ORDER MATTERS (2026-08-07 fix — pi-reweight scenario):
    // a configured pi_weight of 0 is the practice DECLARING PI reweighted
    // (Configuration reweight scenario) → null, never "submitting with 0%".
    const piWeight = w.pi_weight !== undefined ? Number(w.pi_weight) : 25;
    let piPct: number | null;
    let piSource: string;
    if (piWeight === 0) {
      piPct = null;
      piSource = `reweighted (${settings.reweight_scenario ?? 'configured weights'})`;
    } else if (w.use_modeled_pi === true) {
      piPct = w.modeled_pi_percent != null ? Number(w.modeled_pi_percent) : null;
      piSource = piPct != null ? `modeled ${piPct.toFixed(0)}% (Configuration)` : 'modeled — no estimate entered';
    } else if (s.pi_score !== undefined && piWeight > 0) {
      piPct = (Number(s.pi_score) / piWeight) * 100;
      piSource = `calculated ${piPct.toFixed(0)}%`;
    } else {
      piPct = null;
      piSource = 'no PI data';
    }

    // Traditional IA pct: modeled % directly, else calculator totalScore/40.
    const modeledIaPct = w.use_modeled_ia === true && w.modeled_ia_percent != null
      ? Number(w.modeled_ia_percent) : null;
    const traditionalIaPct = modeledIaPct
      ?? (iaScoreQ.data ? (Number((iaScoreQ.data as any).totalScore) / 40) * 100 : null);
    const iaSource = modeledIaPct != null
      ? `modeled ${modeledIaPct.toFixed(0)}% (Configuration)`
      : (iaScoreQ.data ? 'calculated from attestations' : 'no IA data');

    // MVP IA intent: Configuration's modeled estimate is the practice saying
    // "we WILL do improvement activities" — carry it to the MVP binary as the
    // default (owner decision 2026-08-07: Configuration is the persistent home;
    // the what-if panel only overlays temporarily).
    const iaIntentFromConfig = modeledIaPct != null && modeledIaPct > 0 ? true : undefined;

    // Entity scope: subgroup runs read the derived subgroup summaries for quality;
    // cost/PI/pop-health stay group-level (CMS: affiliated-group level for subgroups).
    const qualityCounts = subgroupId != null
      ? summariesToCounts(subgroupSummariesQ.data ?? [])
      : summariesToCounts(summariesQ.data ?? []);
    const costScores = costDashboardToScores(costQ.data);
    const popHealth = extractPopHealth(submission, popHealthManualQ.data ?? []);

    const iaActivityIds: string[] = [...new Set(
      (iaSelectionsQ.data ?? [])
        .map((r: any) => String(r.ia_activity_id ?? r.activity_id ?? ''))
        .filter(Boolean)
    )];

    // Precedence (Option A / IMPLEMENTATION_PLAN §6): the backend resolver is the
    // ONE money number (overrides → org-count rule over MATCHED NPIs → file).
    // Practice Profile Part B is kept in sync after hydrate; it is NOT preferred
    // over the resolver (that left Pathway Report on a stale file value).
    const fin = financialsQ.data;
    const profileCharges = settings.medicare_part_b_charges != null
      ? Number(settings.medicare_part_b_charges) : null;
    const eligCharges = eligibilityQ.data?.practice?.allowed_charges != null
      ? Number(eligibilityQ.data.practice.allowed_charges) : null;

    let allowedCharges: number | null;
    let chargesSource: string;
    if (subgroupId != null) {
      // Subgroup basis = Σ resolved roster NPI values (unmatched NPIs resolve null → excluded)
      const roster = new Set((subgroupsQ.data ?? []).find(sg => sg.id === subgroupId)?.members ?? []);
      const rosterRows = (fin?.npis ?? []).filter(n => roster.has(n.npi));
      const rosterSum = rosterRows.reduce((s, n) => s + (n.resolved ?? 0), 0);
      allowedCharges = rosterRows.length > 0 && rosterSum > 0 ? rosterSum : null;
      chargesSource = allowedCharges != null ? 'sum of subgroup clinicians (resolved)' : 'not available';
    } else if (fin?.tin.resolved != null) {
      allowedCharges = fin.tin.resolved;
      chargesSource = fin.tin.source;
    } else if (profileCharges != null && profileCharges > 0) {
      allowedCharges = profileCharges;
      chargesSource = 'Practice Profile';
    } else {
      allowedCharges = eligCharges;
      chargesSource = eligCharges != null ? 'QPP eligibility file' : 'not available';
    }

    // Prior-year basis: the uploaded score submission's quality category score.
    // Includes bonuses/improvement → reconstructed (confidence-flagged).
    const priorQuality = submission?.quality_score != null ? Number(submission.quality_score) : null;
    const priorCost = submission?.cost_score != null ? Number(submission.cost_score) : null;

    const cpbStored = s.patient_complexity_bonus != null ? Number(s.patient_complexity_bonus) : null;

    const config = dollarsConfigQ.data;
    const inputs: EngineInputs = {
      profile: {
        practiceId: Number(practiceId),
        programYear: year,
        isSmallPractice: settings.is_small_practice === true,
        specialStatuses: eligibilityQ.data?.practice?.special_statuses ?? {},
        multiSpecialty: npiProfilesQ.data?.multiSpecialty ?? false, // Phase C specialty layer
        complexityScore: null, // CPB flows via assumptions.cpbOverride below
        allowedCharges,
      },
      entity: subgroupId != null ? { kind: 'subgroup', subgroupId } : { kind: 'group' },
      qualityCounts,
      costScores,
      iaActivityIds,
      traditionalIaPct,
      piScore: piPct,
      popHealth,
      priorYear: {
        qualityAchievementPct: priorQuality,
        costAchievementPct: priorCost,
        sameIdentifier: priorQuality != null,
        reconstructed: true,
      },
      assumptions: {
        // Defaults derived from Configuration (the persistent assumptions home);
        // page-level what-ifs overlay LAST and reset on leave.
        cpbOverride: cpbStored ?? 0,
        iaWillAttest: iaIntentFromConfig,
        ...assumptions,
      },
      costYear: undefined, // Phase C: carry-forward year surfaced by the cost fetch
      dollarsConfig: config?.low_scenario_input != null
        ? { lowScenarioPct: Number(config.low_scenario_input), scalingFactor: Number(config.scaling_factor ?? 0.74) }
        : undefined,
      dedupCollectionTypes: true, // live composite dedups since 2026-08-07
    };

    const deps: EngineDeps = {
      // Benchmarks are per collection type (CMS guide §2.3; backlog B18) — no fallback to another one.
      getBenchmark: (measureId, submissionMethod) =>
        (dataService.getBenchmarksForMeasure(measureId) ?? []).find(b => b.submissionMethods === submissionMethod) ?? null,
      getMeasureMeta: (measureId) => {
        const meta = (dataService as any).getMeasureMeta?.(measureId);
        const measure = dataService.getCmsMeasure?.(measureId);
        if (!meta && !measure) return null;
        return {
          isOutcome: meta?.isOutcome
            ?? ['outcome', 'intermediateOutcome', 'patientReportedOutcome'].includes(measure?.measureType ?? ''),
          isHighPriority: meta?.isHighPriority ?? measure?.isHighPriority ?? false,
          isInverse: meta?.isInverse ?? measure?.isInverse ?? false,
        };
      },
    };

    const catalog = dataService.getMvps(year);
    const report = rankPathways(inputs, catalog, deps, year);

    return {
      report,
      loading: false,
      ready: true,
      meta: {
        mvpDatasetAvailable,
        measuresWithData: qualityCounts.length,
        popHealthSource: popHealth.m479?.source ?? popHealth.m484?.source ?? 'none',
        priorYearAvailable: priorQuality != null,
        dollarsConfigured: !!inputs.dollarsConfig,
        allowedCharges,
        inputsInUse: {
          isSmallPractice: inputs.profile.isSmallPractice,
          piSource,
          iaSource,
          chargesSource,
          cpb: inputs.assumptions.cpbOverride ?? 0,
        },
      },
    };
  }, [
    loading, practiceId, year, assumptions, subgroupId,
    summariesQ.data, scoresQ.data, weightsQ.data, iaScoreQ.data, iaSelectionsQ.data,
    costQ.data, settingsQ.data, eligibilityQ.data, submissionQ.data,
    popHealthManualQ.data, dollarsConfigQ.data, npiProfilesQ.data,
    subgroupSummariesQ.data, subgroupsQ.data, financialsQ.data,
  ]);
}
